import React, { useState, useEffect } from 'react';
import { useGame } from '../state/gameStore.tsx';
import { BUILDINGS_CONFIG, calculateBuildingUpgradeCost, calculateBuildingUpgradeTimeSec } from '@galaxy/shared';
import type { BuildingType } from '@galaxy/shared';
import {
  CommandCenterIcon,
  MineIcon,
  RefineryIcon,
  CommerceIcon,
  VaultIcon,
  ShipyardIcon,
  LabIcon,
  MetalIcon,
  He3Icon,
  CreditsIcon,
} from '../assets/icons.tsx';

import { QuickBoostModal } from './QuickBoostModal.tsx';

export const BaseView: React.FC = () => {
  const { state, upgradeBuilding } = useGame();
  const [upgradingType, setUpgradingType] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [quickBoostTarget, setQuickBoostTarget] = useState<{ id: string; name: string; remainingSec: number } | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (!state) return null;

  const { buildings, planet, player } = state;
  const currentMetal = planet.resources.metal;
  const currentHe3 = planet.resources.he3;
  const currentCredits = planet.resources.credits;
  const isCyberCitadel = player.equipped_base_theme === 'theme_base_cyber_citadel' || player.equipped_cosmetics?.base_theme === 'theme_base_cyber_citadel';

  const getBuildingIcon = (type: BuildingType) => {
    switch (type) {
      case 'command_center': return <CommandCenterIcon size={32} />;
      case 'metal_mine': return <MineIcon size={32} />;
      case 'he3_refinery': return <RefineryIcon size={32} />;
      case 'commerce_hub': return <CommerceIcon size={32} />;
      case 'storage_vault': return <VaultIcon size={32} />;
      case 'shipyard': return <ShipyardIcon size={32} />;
      case 'research_lab': return <LabIcon size={32} />;
    }
  };

  const handleUpgrade = async (type: BuildingType) => {
    try {
      setUpgradingType(type);
      await upgradeBuilding(type);
    } catch {
      // Handled in store
    } finally {
      setUpgradingType(null);
    }
  };

  return (
    <div style={{
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      background: isCyberCitadel ? 'radial-gradient(ellipse at top, rgba(59, 130, 246, 0.08) 0%, transparent 70%)' : undefined,
    }}>
      {/* Section Title & Theme Badge */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-bright)', letterSpacing: 0.5 }}>
              QUẢN LÝ CĂN CỨ VŨ TRỤ
            </h2>
            {isCyberCitadel && (
              <span style={{
                background: 'rgba(59, 130, 246, 0.2)',
                border: '1px solid #3b82f6',
                color: '#60a5fa',
                fontSize: 10,
                fontWeight: 700,
                padding: '2px 6px',
                borderRadius: 4,
                boxShadow: '0 0 8px rgba(59, 130, 246, 0.4)',
              }}>
                🏛️ CYBER CITADEL
              </span>
            )}
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>
            Nâng cấp các công trình để gia tăng sản lượng tài nguyên và sức mạnh hạm đội
          </p>
        </div>
      </div>

      {/* Building Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {buildings.map((b) => {
          const cfg = BUILDINGS_CONFIG[b.type];
          const cost = calculateBuildingUpgradeCost(b.type, b.level);
          const timeSec = calculateBuildingUpgradeTimeSec(b.type, b.level);

          const isUpgrading = b.upgrade_finish_at && b.upgrade_finish_at > now;
          const remainingSec = isUpgrading ? Math.max(0, Math.ceil((b.upgrade_finish_at! - now) / 1000)) : 0;

          const canAfford = currentMetal >= cost.metal && currentHe3 >= cost.he3 && currentCredits >= cost.credits;
          const isMaxLevel = b.level >= cfg.max_level;

          return (
            <div key={b.id} className="sci-card" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {/* Icon Container */}
              <div style={{
                width: 52,
                height: 52,
                borderRadius: 6,
                background: 'var(--bg-panel-subtle)',
                border: '1px solid var(--border-panel)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                {getBuildingIcon(b.type)}
              </div>

              {/* Building Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-bright)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {cfg.name}
                  </h3>
                  <span className="mono" style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'var(--accent-cyan)',
                    background: 'rgba(0, 229, 255, 0.1)',
                    border: '1px solid rgba(0, 229, 255, 0.3)',
                    padding: '1px 6px',
                    borderRadius: 3,
                  }}>
                    CẤP {b.level}
                  </span>
                </div>

                <p style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 6, lineHeight: 1.3 }}>
                  {cfg.description}
                </p>

                {/* Costs & Upgrade Button */}
                {isUpgrading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div className="sci-progress" style={{ flex: 1, minWidth: 80 }}>
                      <div className="sci-progress-bar" style={{ width: '100%' }} />
                    </div>
                    <span className="mono" style={{ fontSize: 12, color: 'var(--accent-amber)', fontWeight: 700 }}>
                      ⏳ {remainingSec}s
                    </span>
                    <button
                      className="sci-btn"
                      onClick={() => setQuickBoostTarget({ id: `${state?.planet.id}:${b.type}`, name: cfg.name, remainingSec })}
                      style={{
                        padding: '2px 8px',
                        fontSize: 10,
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.2) 0%, rgba(2, 132, 199, 0.3) 100%)',
                        color: 'var(--accent-cyan)',
                        borderColor: 'var(--accent-cyan)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ⚡ TĂNG TỐC
                    </button>
                  </div>
                ) : isMaxLevel ? (
                  <span style={{ fontSize: 12, color: 'var(--status-green)', fontWeight: 700 }}>
                    ĐÃ ĐẠT CẤP ĐỘ TỐI ĐA
                  </span>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                    {/* Resource Costs */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 2, color: currentMetal >= cost.metal ? 'var(--text-bright)' : 'var(--status-red)' }}>
                        <MetalIcon size={12} />
                        <span className="mono">{cost.metal}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 2, color: currentHe3 >= cost.he3 ? 'var(--text-bright)' : 'var(--status-red)' }}>
                        <He3Icon size={12} />
                        <span className="mono">{cost.he3}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 2, color: currentCredits >= cost.credits ? 'var(--text-bright)' : 'var(--status-red)' }}>
                        <CreditsIcon size={12} />
                        <span className="mono">{cost.credits}</span>
                      </div>
                      <span className="mono" style={{ color: 'var(--text-dim)' }}>
                        ({timeSec}s)
                      </span>
                    </div>

                    {/* Upgrade button */}
                    <button
                      className="sci-btn"
                      disabled={!canAfford || upgradingType === b.type}
                      onClick={() => handleUpgrade(b.type)}
                      style={{ padding: '6px 12px', fontSize: 12 }}
                    >
                      {upgradingType === b.type ? 'Đang xử lý...' : 'NÂNG CẤP'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {quickBoostTarget && (
        <QuickBoostModal
          targetType="building"
          targetId={quickBoostTarget.id}
          targetName={quickBoostTarget.name}
          remainingSeconds={quickBoostTarget.remainingSec}
          onClose={() => setQuickBoostTarget(null)}
          onSuccess={() => setQuickBoostTarget(null)}
        />
      )}
    </div>
  );
};
